import {
  Activity,
  ArrowDownUp,
  ArrowLeftRight,
  ArrowRightLeft,
  BadgePercent,
  Banknote,
  BarChart3,
  Barcode,
  BellRing,
  Bookmark,
  Boxes,
  Briefcase,
  Building2,
  CalendarClock,
  ChartPie,
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsLeft,
  ChevronsRight,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Contact2,
  CreditCard,
  Download,
  Factory,
  FileBarChart,
  FileSignature,
  FileText,
  Fingerprint,
  FolderOpen,
  FolderTree,
  Gauge,
  Gift,
  Globe,
  Images,
  Home,
  Landmark,
  Languages,
  MapPin,
  LayoutDashboard,
  ListChecks,
  LogOut,
  LineChart,
  Menu,
  MessageCircle,
  Navigation,
  Newspaper,
  Package,
  Printer,
  Receipt,
  ScanLine,
  Ruler,
  Search,
  Send,
  Settings,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Smartphone,
  Sparkles,
  Star,
  Store,
  Tags,
  Target,
  Ticket,
  ToggleLeft,
  TrendingUp,
  Truck,
  Undo2,
  UserCheck,
  Users,
  UsersRound,
  Wallet,
  Warehouse,
} from 'lucide-react';
import { type ComponentType, type ReactNode, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Toaster,
  TooltipProvider,
} from '../components/shadcn/index.js';
import { BrandFavicon, BrandMark, RouteTransition, ThemeToggle, ZoomControls } from '../components/ui/index.js';
import { LocaleFlag } from '../components/ui/LocaleFlag.js';
import { AILauncher, AILauncherButton } from '../features/ai/AILauncher.js';
import { CommandPalette } from '../features/common/CommandPalette.js';
import { PaletteSwitcher } from '../features/common/PaletteSwitcher.js';
import { RateSwitcher } from '../features/common/RateSwitcher.js';
import { recordMenuVisit, TopNavMenu } from '../features/common/TopNavMenu.js';
import { NotificationsBell } from '../features/notifications/NotificationsBell.js';
import { BillingBanner } from '../features/platform/BillingBanner.js';
import { ImpersonationBanner } from '../features/platform/ImpersonationBanner.js';
import { DirectionScope, setLocale, setPageDirection, useDirection } from '../hooks/useDirection.js';
import { useNumerals } from '../hooks/useNumerals.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../lib/authStore.js';
import { getBrand } from '../lib/brandClient.js';
import { cn } from '../lib/cn.js';
import { useCurrenciesStore } from '../lib/currenciesStore.js';
import { useEnabledLanguages, useLanguagesStore } from '../lib/languagesStore.js';
import { moduleEnabled, SECTION_TO_MODULE, type ModuleKey } from '../lib/modules.js';
import { loadModules, useEnabledModules, useModulesStore } from '../lib/modulesStore.js';
import { useSiteContextStore } from '../lib/siteContextStore.js';

type Icon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;

export interface NavItem {
  to: string;
  labelKey: string;
  icon: Icon;
  ns?: string;
  /** When set, the item only shows if the user holds this permission. */
  perm?: string;
  /**
   * When set, ANY ONE of these is enough. For a page that merges what used
   * to be two — holding either half's permission means there is something
   * on it worth opening.
   */
  anyPerm?: string[];
  /** When set, the item only shows if this toggleable module is enabled
   *  (item-level gate, for items that live inside an always-on section). */
  module?: ModuleKey;
}

export interface NavSection {
  labelKey: string;
  /** Section icon shown in the caption (chip), like the items inside it. */
  icon?: Icon;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    labelKey: 'nav.section.overview',
    icon: LayoutDashboard,
    items: [{ to: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard }],
  },
  {
    labelKey: 'nav.section.files',
    icon: FolderOpen,
    items: [{ to: '/files', labelKey: 'nav.files', icon: FolderOpen, perm: 'attachments.read' }],
  },
  {
    labelKey: 'nav.section.accounting',
    icon: Landmark,
    items: [
      { to: '/accounting/accounts', labelKey: 'nav.accounts', icon: Landmark, perm: 'accounts.read' },
      { to: '/accounting/journals', labelKey: 'nav.journals', icon: Newspaper, perm: 'gl.read' },
      { to: '/accounting/transactions', labelKey: 'nav.transactions', icon: ArrowDownUp, perm: 'gl.read' },
      { to: '/accounting/vouchers/receipts', labelKey: 'nav.receiptVouchers', icon: Banknote, module: 'vouchers', perm: 'gl.read' },
      { to: '/accounting/vouchers/payments', labelKey: 'nav.paymentVouchers', icon: Wallet, module: 'vouchers', perm: 'gl.read' },
      { to: '/accounting/transfers', labelKey: 'nav.treasury', icon: ArrowLeftRight, module: 'treasury', perm: 'gl.read' },
      { to: '/accounting/exchange-rates', labelKey: 'nav.exchangeRates', icon: ArrowRightLeft, perm: 'exchange_rates.read' },
      { to: '/accounting/trial-balance', labelKey: 'nav.trialBalance', icon: BarChart3, perm: 'gl.read' },
      { to: '/accounting/periods', labelKey: 'nav.periods', icon: CalendarClock, perm: 'periods.read' },
    ],
  },
  {
    labelKey: 'nav.section.inventory',
    icon: Boxes,
    items: [
      { to: '/inventory/items', labelKey: 'nav.items', icon: Tags, perm: 'items.read' },
      { to: '/inventory/labels', labelKey: 'nav.labels', icon: Barcode, perm: 'items.read' },
      { to: '/inventory/categories', labelKey: 'nav.categories', icon: FolderTree, perm: 'items.read' },
      { to: '/inventory/brands', labelKey: 'nav.brands', icon: Bookmark, perm: 'items.read' },
      { to: '/inventory/units', labelKey: 'nav.units', icon: Ruler, perm: 'items.read' },
      { to: '/inventory/warehouses', labelKey: 'nav.warehouses', icon: Warehouse, perm: 'warehouses.read' },
      { to: '/inventory/stock', labelKey: 'nav.stock', icon: Boxes, perm: 'stock.read' },
      { to: '/inventory/stocktakes', labelKey: 'nav.stocktake', icon: ClipboardCheck, module: 'stocktake', perm: 'stock.read' },
      { to: '/inventory/batches', labelKey: 'nav.batches', icon: CalendarClock, module: 'batches', perm: 'stock.read' },
      { to: '/inventory/transfers', labelKey: 'nav.stockTransfers', icon: ArrowLeftRight, perm: 'stock.transfer.read' },
      { to: '/inventory/movements', labelKey: 'nav.movements', icon: Truck, perm: 'stock.read' },
    ],
  },
  {
    // Whole-section module gate via SECTION_TO_MODULE — off, the section is gone.
    labelKey: 'nav.section.manufacturing',
    icon: Factory,
    items: [
      { to: '/manufacturing/orders', labelKey: 'nav.productionOrders', icon: Factory, perm: 'manufacturing.read' },
      { to: '/manufacturing/boms', labelKey: 'nav.boms', icon: ListChecks, perm: 'manufacturing.read' },
    ],
  },
  {
    // Every people DIRECTORY lives here, whichever department owns the workflow.
    // Workspaces (e.g. the mandob field route) and boards (deliveries) stay put —
    // this section is "who are our people", not "what are they doing".
    labelKey: 'nav.section.persons',
    icon: UsersRound,
    items: [
      { to: '/sales/parties', labelKey: 'nav.customers', icon: Contact2, perm: 'parties.read' },
      { to: '/purchasing/suppliers', labelKey: 'nav.suppliers', icon: Contact2, perm: 'purchase_orders.read' },
      // The Persons section itself is always on, so items that used to inherit a
      // module gate from their old section carry it explicitly (HR → 'hr',
      // Mandobs → 'mandob', Drivers → 'delivery'). Without this, moving them here
      // would silently un-hide them for tenants with those modules switched off.
      { to: '/persons/drivers', labelKey: 'nav.drivers', icon: Truck, module: 'delivery', perm: 'users.read' },
      { to: '/mandob', labelKey: 'nav.mandobs', icon: Truck, module: 'mandob', perm: 'mandob.manage' },
      { to: '/hr/employees', labelKey: 'nav.employees', icon: UsersRound, module: 'hr', perm: 'hr.employees.read' },
      { to: '/settings/users', labelKey: 'nav.admins', icon: Users, perm: 'users.read' },
      { to: '/settings/roles', labelKey: 'nav.roles', icon: ShieldCheck, perm: 'roles.write' },
    ],
  },
  {
    labelKey: 'nav.section.sales',
    icon: ShoppingBag,
    items: [
      // First, because a quote comes BEFORE the order it turns into — the section now
      // reads in the order the paperwork actually happens: quote → order → invoice.
      // `module` is load-bearing here: it used to be gated by owning a whole section
      // (SECTION_TO_MODULE), and Sales is always-on, so without this the Settings →
      // Modules switch for quotations would silently stop hiding it.
      { to: '/quotations', labelKey: 'nav.quotations', icon: FileSignature, module: 'quotations', perm: 'quotations.read' },
      // Invoices and orders are two tabs of ONE page now — the sidebar had
      // grown long enough that finding anything in it was the work, and these
      // two belong together anyway (an order becomes an invoice). `anyPerm`
      // because either permission is enough to have something to look at.
      { to: '/sales/invoices', labelKey: 'nav.salesDocuments', icon: Receipt, anyPerm: ['invoices.read', 'sales_orders.read'] },
      { to: '/sales/returns', labelKey: 'nav.returns', icon: Undo2, module: 'returns', perm: 'invoices.read' },
      { to: '/sales/price-lists', labelKey: 'nav.priceLists', icon: Coins, module: 'pricing', perm: 'items.read' },
      { to: '/sales/discounts', labelKey: 'nav.discounts', icon: BadgePercent, module: 'discounts', perm: 'discounts.read' },
      { to: '/sales/combos', labelKey: 'nav.combos', icon: Package, module: 'combos', perm: 'items.read' },
      { to: '/sales/customer-requests', labelKey: 'nav.customerRequests', icon: ClipboardCheck, module: 'pricing', perm: 'parties.read' },
    ],
  },
  {
    labelKey: 'nav.section.purchasing',
    icon: ShoppingCart,
    items: [
      { to: '/purchasing/bills', labelKey: 'nav.bills', icon: FileText, perm: 'bills.read' },
      { to: '/payments', labelKey: 'nav.payments', icon: Wallet, perm: 'payments.read' },
    ],
  },
  {
    labelKey: 'nav.section.expenses',
    icon: Banknote,
    items: [{ to: '/expenses', labelKey: 'nav.expenses', icon: Banknote, perm: 'expenses.read' }],
  },
  {
    labelKey: 'nav.section.reports',
    icon: BarChart3,
    items: [
      { to: '/reports/statistics', labelKey: 'nav.statistics', icon: LineChart, perm: 'reports.financial' },
      { to: '/reports/ai', labelKey: 'nav.aiReports', icon: Sparkles, perm: 'ai_reports.read', module: 'ai_reports' },
      // Not `reports.read`: cost is the one figure a shop keeps off the screens
      // of people who may read every other report.
      { to: '/reports/profit', labelKey: 'nav.profitReport', icon: TrendingUp, perm: 'profits.report' },
      { to: '/reports/profit-and-loss', labelKey: 'nav.pnl', icon: ChartPie, perm: 'reports.financial' },
      { to: '/reports/balance-sheet', labelKey: 'nav.balanceSheet', icon: FileBarChart, perm: 'reports.financial' },
      { to: '/reports/ar-aging', labelKey: 'nav.arAging', icon: Coins, perm: 'reports.read' },
      { to: '/reports/ap-aging', labelKey: 'nav.apAging', icon: CreditCard, perm: 'reports.read' },
      { to: '/reports/expenses', labelKey: 'nav.expensesReport', icon: Banknote, perm: 'expenses.read' },
      { to: '/reports/stock', labelKey: 'nav.stockReport', icon: Boxes, perm: 'stock.report' },
      { to: '/reports/cashback', labelKey: 'nav.cashbackReport', icon: Gift, perm: 'reports.read', module: 'cashback' },
    ],
  },
  {
    labelKey: 'nav.section.hr',
    icon: UsersRound,
    items: [
      { to: '/hr/organization', labelKey: 'nav.organization', icon: Building2, perm: 'hr.org.read' },
      { to: '/hr/payroll', labelKey: 'nav.payroll', icon: Wallet, perm: 'hr.payroll.read' },
      { to: '/hr/attendance', labelKey: 'nav.attendance', icon: Fingerprint, anyPerm: ['hr.time.read', 'hr.time.approve', 'hr.self.time.entry', 'hr.admin'] },
      { to: '/hr/attendance/kiosk', labelKey: 'nav.attendanceKiosk', icon: ScanLine, anyPerm: ['hr.time.kiosk', 'hr.time.approve'] },
      { to: '/hr/leave', labelKey: 'nav.leave', icon: CalendarClock, perm: 'hr.leave.read' },
    ],
  },
  {
    labelKey: 'nav.section.crm',
    icon: Briefcase,
    items: [
      { to: '/crm/accounts', labelKey: 'nav.accounts', icon: Briefcase, perm: 'crm.accounts.read' },
      { to: '/crm/contacts', labelKey: 'nav.contacts', icon: Contact2, perm: 'crm.contacts.read' },
      { to: '/crm/leads', labelKey: 'nav.leads', icon: Target, perm: 'crm.leads.read' },
      { to: '/crm/opportunities', labelKey: 'nav.opportunities', icon: Gauge, perm: 'crm.opportunities.read' },
      { to: '/crm/activities', labelKey: 'nav.activities', icon: Activity, perm: 'crm.activities.read' },
    ],
  },
  {
    labelKey: 'nav.section.installments',
    icon: CalendarClock,
    items: [
      { to: '/installments/plans', labelKey: 'nav.installmentPlans', icon: ClipboardList, perm: 'installments.read' },
      { to: '/installments/reports', labelKey: 'nav.installmentReports', icon: BarChart3, perm: 'installments.read' },
    ],
  },
  {
    labelKey: 'nav.section.pos',
    icon: Store,
    items: [
      { to: '/pos/register', labelKey: 'pos:nav.kiosk', icon: Store, ns: 'pos', perm: 'pos.read' },
      { to: '/pos/sales', labelKey: 'pos:nav.sales', icon: Receipt, ns: 'pos', perm: 'pos.read' },
      { to: '/pos/terminals', labelKey: 'pos:nav.terminals', icon: ShoppingBag, ns: 'pos', perm: 'pos.terminal.admin' },
      { to: '/pos/shifts', labelKey: 'pos:nav.shifts', icon: Wallet, ns: 'pos', perm: 'pos.read' },
    ],
  },
  {
    labelKey: 'nav.section.coupons',
    icon: Ticket,
    items: [
      { to: '/coupons', labelKey: 'nav.coupons', icon: Ticket, perm: 'coupons.read' },
      { to: '/coupons/redemptions', labelKey: 'nav.couponRedemptions', icon: ClipboardList, perm: 'coupons.read' },
    ],
  },
  {
    labelKey: 'nav.section.cashback',
    icon: Gift,
    items: [
      { to: '/cashback', labelKey: 'nav.cashback', icon: Gift, perm: 'cashback.read' },
      { to: '/cashback/customers', labelKey: 'nav.cashbackCustomers', icon: Users, perm: 'cashback.read' },
      { to: '/cashback/ledger', labelKey: 'nav.cashbackLedger', icon: ClipboardList, perm: 'cashback.read' },
    ],
  },
  {
    labelKey: 'nav.section.mandob',
    icon: Navigation,
    items: [
      { to: '/field', labelKey: 'nav.fieldWorkspace', icon: Navigation, perm: 'mandob.read' },
      // `perm` MUST equal the route's own guard (sales_orders.read), or the link shows
      // for everyone and dead-ends on the access card. A rep holds mandob.* only, so
      // this stays an office view — a rep seeing every rep's book would be a leak.
      { to: '/mandob/orders', labelKey: 'nav.mandobOrders', icon: ShoppingBag, module: 'mandob', perm: 'sales_orders.read' },
    ],
  },
  {
    labelKey: 'nav.section.cms',
    icon: Globe,
    items: [
      { to: '/cms', labelKey: 'nav.cmsSettings', icon: Store, perm: 'cms.read' },
      { to: '/cms/products', labelKey: 'nav.cmsProducts', icon: ShoppingBag, perm: 'cms.read' },
      { to: '/cms/store-categories', labelKey: 'nav.cmsStoreCategories', icon: FolderTree, perm: 'cms.read' },
      { to: '/cms/orders', labelKey: 'nav.cmsOrders', icon: ClipboardList, perm: 'cms.read' },
      { to: '/cms/pages', labelKey: 'nav.cmsPages', icon: FileText, perm: 'cms.read' },
      { to: '/cms/slides', labelKey: 'nav.cmsSlides', icon: Images, perm: 'cms.read' },
      { to: '/cms/reviews', labelKey: 'nav.cmsReviews', icon: Star, module: 'reviews', perm: 'cms.read' },
      { to: '/cms/navigation', labelKey: 'nav.cmsNavigation', icon: Menu, perm: 'cms.read' },
    ],
  },
  {
    labelKey: 'nav.section.delivery',
    icon: Truck,
    items: [
      { to: '/delivery', labelKey: 'nav.deliveryBoard', icon: Truck, perm: 'delivery.read' },
      { to: '/delivery/mine', labelKey: 'nav.myDeliveries', icon: Truck, perm: 'delivery.deliver' },
      { to: '/delivery/cities', labelKey: 'nav.deliveryCities', icon: MapPin, module: 'cms', perm: 'cms.read' },
    ],
  },
  {
    labelKey: 'nav.section.marketing',
    icon: Sparkles,
    items: [
      { to: '/marketing/stories', labelKey: 'nav.stories', icon: Sparkles, perm: 'stories.read' },
      { to: '/marketing/highlights', labelKey: 'nav.highlights', icon: Star, perm: 'stories.read' },
    ],
  },
  {
    labelKey: 'nav.section.settings',
    icon: Settings,
    items: [
      { to: '/settings/profile', labelKey: 'nav.profile', icon: UserCheck },
      { to: '/settings/downloads', labelKey: 'nav.downloads', icon: Download, perm: 'settings.read' },
      { to: '/settings/tenant', labelKey: 'nav.tenant', icon: Settings, perm: 'tenants.read' },
      { to: '/settings/numbering', labelKey: 'nav.numbering', icon: Tags, perm: 'settings.write' },
      { to: '/settings/inventory', labelKey: 'nav.inventorySettings', icon: Boxes, perm: 'settings.write' },
      { to: '/settings/printing', labelKey: 'nav.printing', icon: Printer, perm: 'settings.write' },
      { to: '/settings/custom-fields', labelKey: 'nav.customFields', icon: SlidersHorizontal, module: 'custom_fields', perm: 'settings.write' },
      { to: '/settings/modules', labelKey: 'nav.modules', icon: ToggleLeft, perm: 'settings.write' },
      { to: '/settings/languages', labelKey: 'nav.languages', icon: Languages, perm: 'settings.write' },
      { to: '/settings/currencies', labelKey: 'nav.currencies', icon: Coins, perm: 'settings.write' },
      { to: '/settings/whatsapp', labelKey: 'nav.whatsapp', icon: MessageCircle, perm: 'whatsapp.manage' },
      { to: '/settings/telegram', labelKey: 'nav.telegram', icon: Send, perm: 'telegram.manage' },
      { to: '/settings/push', labelKey: 'nav.push', icon: BellRing, perm: 'push.manage' },
      // Folded in from their own one-item sections (owner's call): the send
      // screen and the studio are used from Settings anyway, and a section
      // with a single page costs a whole tile.
      { to: '/notifications/send', labelKey: 'nav.sendNotification', icon: Send, perm: 'push.manage' },
      { to: '/settings/ai', labelKey: 'nav.ai', icon: Sparkles, module: 'ai', perm: 'ai.admin' },
      // The section-level imagegen gate rides along as an item-level one.
      { to: '/imagegen', labelKey: 'nav.imageStudio', icon: Images, module: 'imagegen', perm: 'imagegen.use' },
      { to: '/settings/imagegen', labelKey: 'nav.imageSettings', icon: Images, module: 'imagegen', perm: 'imagegen.admin' },
      { to: '/settings/fib', labelKey: 'nav.fib', icon: CreditCard, module: 'fib', perm: 'settings.write' },
      { to: '/settings/otp', labelKey: 'nav.otp', icon: Smartphone, module: 'otp', perm: 'settings.write' },
      { to: '/settings/audit-log', labelKey: 'nav.auditLog', icon: ShieldCheck, module: 'audit', perm: 'audit_logs.read' },
    ],
  },
];

/** Titlebar shortcuts shown on the POS register; gated like their nav items. */
const POS_QUICK_LINKS: { to: string; labelKey: string; icon: Icon; perm: string; module?: ModuleKey }[] = [
  { to: '/inventory/transfers/new', labelKey: 'actions.posStockTransfer', icon: ArrowLeftRight, perm: 'stock.transfer.write' },
  { to: '/sales/returns', labelKey: 'nav.returns', icon: Undo2, perm: 'invoices.read', module: 'returns' },
];

const NAV_OPEN_KEY = 'erp-nav-open';
const NAV_RAIL_KEY = 'erp-nav-rail';
/**
 * The persisted nav state, under the key it has always used.
 *
 * A v1 value is a bare `string[]` written by the version that ADDED the current route's
 * section on every navigation and never removed one — so an existing user's array is an
 * accumulated list of everywhere they have ever been, not a choice they made. It is
 * deliberately DISCARDED rather than migrated: adopting it would reproduce exactly the
 * wall of open sections this change exists to fix.
 */
function loadNavState(): { open: string[]; closed: string[] } {
  const empty = { open: [], closed: [] };
  try {
    const raw = window.localStorage.getItem(NAV_OPEN_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return empty;                       // v1 — drop it
    const o = parsed as { v?: number; open?: unknown; closed?: unknown };
    return {
      open: Array.isArray(o.open) ? (o.open as string[]) : [],
      closed: Array.isArray(o.closed) ? (o.closed as string[]) : [],
    };
  } catch {
    return empty;
  }
}

export function AppLayout({ children, kiosk = false }: { children: ReactNode; kiosk?: boolean }): JSX.Element {
  const { t, i18n } = useTranslation();
  const dir = useDirection();
  const location = useLocation();
  const navigate = useNavigate();
  const tenant = useAuthStore((s) => s.tenant);
  // On the tenant's app domain, prefer their own tagline (Store settings).
  const siteTagline = useSiteContextStore((s) => (s.kind === 'app' ? s.tenant?.tagline : null));
  const user = useAuthStore((s) => s.user);
  const { numerals, setNumerals } = useNumerals();
  // Global ⌘K command palette (jump to pages, run actions, search records).
  const [cmdOpen, setCmdOpen] = useState(false);
  // AI assistant panel — toggled from the titlebar button (beside Home/POS).
  const [aiOpen, setAiOpen] = useState(false);
  // Pages follow the language like the shell; a page that must stay
  // left-to-right (the item form) sets its own DirectionScope. The root-mounted
  // confirm dialog learns the pages' direction from here.
  useEffect(() => {
    setPageDirection(dir);
    return () => setPageDirection(null);
  }, [dir]);

  // Load the tenant's module on/off flags once, then hide disabled sections.
  const enabledModules = useEnabledModules(tenant?.id);
  useEffect(() => {
    if (tenant?.id) void loadModules(tenant.id);
  }, [tenant?.id]);

  // Quick-open POS register from the titlebar when the POS module is on.
  const posEnabled = moduleEnabled(enabledModules, 'pos');
  // Storefront ("front end") quick-open: when the CMS/store module is on, load
  // the public store slug so the titlebar can link straight to the live site.
  const cmsEnabled = moduleEnabled(enabledModules, 'cms');
  const [storeSlug, setStoreSlug] = useState<string | null>(null);
  useEffect(() => {
    if (!cmsEnabled) { setStoreSlug(null); return; }
    void api()
      .get<{ slug?: string }>('/cms/settings')
      .then((r) => setStoreSlug(r.data.slug ?? null))
      .catch(() => setStoreSlug(null));
  }, [cmsEnabled]);
  const permissions = useAuthStore((s) => s.permissions);
  const visibleNav = NAV
    .filter((s) => {
      const m = SECTION_TO_MODULE[s.labelKey];
      return !m || moduleEnabled(enabledModules, m);
    })
    // Hide items the user lacks permission for, or whose module is disabled
    // (items with no `perm`/`module` always show).
    .map((s) => ({
      ...s,
      items: s.items.filter(
        (i) =>
          (!i.perm || permissions.includes(i.perm)) &&
          (!i.anyPerm || i.anyPerm.some((p) => permissions.includes(p))) &&
          (!i.module || moduleEnabled(enabledModules, i.module)),
      ),
    }))
    .filter((s) => s.items.length > 0);

  // Feed the launcher's "Recent" row: every route change records the nav PAGE
  // the path belongs to. Keyed on pathname only — visibleNav is rebuilt each
  // render, and it is the path changing that means a visit happened.
  useEffect(() => {
    recordMenuVisit(visibleNav, location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Responsive nav: docked + collapsible on desktop, off-canvas drawer on
  // mobile. Open by default on desktop (≥1024px), closed on mobile; tracks
  // viewport changes so a resize across the breakpoint does the right thing.
  const DESKTOP = '(min-width: 1024px)';
  const [navOpen, setNavOpen] = useState<boolean>(
    () => typeof window === 'undefined' || window.matchMedia(DESKTOP).matches,
  );
  const [isDesktop, setIsDesktop] = useState<boolean>(
    () => typeof window === 'undefined' || window.matchMedia(DESKTOP).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP);
    const onChange = (): void => { setNavOpen(mq.matches); setIsDesktop(mq.matches); };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // On mobile, tapping a nav link should close the drawer; on desktop it stays.
  const closeNavOnMobile = useCallback((): void => {
    if (typeof window !== 'undefined' && !window.matchMedia(DESKTOP).matches) {
      setNavOpen(false);
    }
  }, []);

  /**
   * Rail mode: the sidebar shrunk to a column of SECTION icons, with the items of a
   * section shown in a flyout on click.
   *
   * One icon per SECTION, never per item — 80 item icons is 3,200px of column, whereas
   * the ~17 sections a full-permission owner sees fit the viewport with room to spare.
   * Desktop only: on mobile the sidebar is already an off-canvas drawer, which solves
   * the same problem better, so the rail is not applied below the breakpoint.
   */
  const [railPref, setRailPref] = useState<boolean>(() => {
    try { return window.localStorage.getItem(NAV_RAIL_KEY) === '1'; } catch { return false; }
  });
  /**
   * The preference is remembered across breakpoints but only ACTED ON above the
   * desktop breakpoint — the rail's CSS lives in a min-width query, so without this
   * a phone would render the icon-only markup at the drawer's full 300px width:
   * seventeen unlabelled icons and not one readable link.
   */
  const rail = railPref && isDesktop;
  const setRail = setRailPref;
  useEffect(() => {
    // The PREFERENCE, not the derived value — persisting `rail` would silently
    // erase the choice the moment the window narrowed past the breakpoint.
    try { window.localStorage.setItem(NAV_RAIL_KEY, railPref ? '1' : '0'); } catch { /* private mode */ }
  }, [railPref]);
  /** Which section's flyout is open, and where to put it (viewport coords). */
  const [flyout, setFlyout] = useState<{ key: string; top: number; left: number } | null>(null);
  useEffect(() => { if (!rail) setFlyout(null); }, [rail]);

  // Collapsible nav sections ("submenus") — keeps the long sidebar tidy.
  // TWO sets, because "open" and "not closed" are different things: the section you are
  // currently in is open without being pinned, and a deliberate fold of it has to be
  // recorded somewhere or it cannot survive navigating away and back.
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(loadNavState().open));
  const [closedGroups, setClosedGroups] = useState<Set<string>>(() => new Set(loadNavState().closed));
  const toggleGroup = useCallback((key: string, currentlyOpen: boolean): void => {
    if (currentlyOpen) {
      setOpenGroups((prev) => { const n = new Set(prev); n.delete(key); return n; });
      setClosedGroups((prev) => new Set(prev).add(key));
    } else {
      setOpenGroups((prev) => new Set(prev).add(key));
      setClosedGroups((prev) => { const n = new Set(prev); n.delete(key); return n; });
    }
  }, []);
  /**
   * Fold every section in one click.
   *
   * The section you are standing in has to be closed EXPLICITLY, not merely
   * un-pinned: it is open by derivation (it owns the current route), so leaving it
   * out of `closed` would leave one section stubbornly open and make the button look
   * broken. Only the sections actually on screen are recorded — writing the whole NAV
   * would persist keys for modules this tenant has switched off.
   */
  const collapseAll = useCallback((sections: NavSection[]): void => {
    setOpenGroups(new Set());
    setClosedGroups(new Set(sections.map((s) => s.labelKey)));
  }, []);
  useEffect(() => {
    try {
      window.localStorage.setItem(
        NAV_OPEN_KEY,
        JSON.stringify({ v: 2, open: [...openGroups], closed: [...closedGroups] }),
      );
    } catch {
      /* ignore */
    }
  }, [openGroups, closedGroups]);

  const onLogout = async () => {
    try {
      await api().post('/auth/logout', {});
    } finally {
      useAuthStore.getState().clear();
      useModulesStore.getState().clear();
      navigate('/login');
    }
  };

  // The active nav item is the one whose `to` is the LONGEST prefix of the
  // current path, so a deeper item (e.g. /coupons/redemptions) wins over its
  // parent (/coupons) — otherwise both highlight and the title resolves wrong.
  const activePath = NAV.flatMap((s) => s.items)
    .map((i) => i.to)
    .filter((to) => location.pathname === to || location.pathname.startsWith(`${to}/`))
    .reduce((best, to) => (to.length > best.length ? to : best), '');
  const isActive = (to: string) => to !== '' && to === activePath;

  /**
   * The section owning the current route is expanded — DERIVED, never stored.
   *
   * It used to be written into the persisted open-set on every navigation, and nothing
   * ever removed one, so the set only grew: visit eight areas and eight sections are
   * open forever, across reloads and logouts. It also quietly undid a deliberate
   * collapse — fold Sales while on /sales/orders, come back later, and it was open
   * again, so the sections you most want folded were the ones that refused to stay
   * folded. Deriving it at render keeps the convenience and drops both faults.
   */
  const activeSection = visibleNav.find((s) => s.items.some((i) => isActive(i.to)))?.labelKey;
  const isGroupOpen = (key: string): boolean => (
    openGroups.has(key) || (key === activeSection && !closedGroups.has(key))
  );

  const brandName = getBrand().name.full;

  // Dynamic content-language switcher: load the tenant's catalog once, then
  // offer the enabled languages. Chrome falls back to English for languages
  // without a bundled UI catalog.
  const enabledLanguages = useEnabledLanguages();
  const curLang = i18n.language.split('-')[0] ?? 'en';
  useEffect(() => { void useLanguagesStore.getState().load(); }, []);
  // Boot the currencies registry so formatMoney honors backend per-currency
  // decimals (e.g. IQD → 0) everywhere, not the browser's CLDR table.
  useEffect(() => { void useCurrenciesStore.getState().load(); }, []);

  // Page title shown in the titlebar — the label of the active nav item.
  const pageTitle =
    NAV.flatMap((s) => s.items).find((item) => isActive(item.to)) ?? null;
  const title = pageTitle ? t(pageTitle.labelKey) : (tenant?.name ?? brandName);

  // Browser TAB title = the page, then the store's OWN name (per tenant), not
  // the generic app name: "Sales invoices · Duhok Stationery". With only the
  // store name every tab read the same — in a browser, and in the manager exe's
  // tab strip, which shows exactly this. Host-resolved siteName covers the store
  // before the session tenant loads. The notifications bell rides its unread
  // badge "(N) " on top; it is kept across navigations rather than wiped.
  const siteName = useSiteContextStore((s) => s.tenant?.name ?? null);
  const pageLabel = pageTitle ? title : null;
  useEffect(() => {
    const base = tenant?.name || siteName || brandName;
    if (!base) return;
    const badge = /^\(\d+\+?\)\s/.exec(document.title)?.[0] ?? '';
    document.title = `${badge}${pageLabel ? `${pageLabel} · ${base}` : base}`;
  }, [tenant?.name, siteName, brandName, pageLabel]);

  // Direct-URL guard: a disabled module's pages redirect to the dashboard
  // (the nav already hides them). Allowed while flags are still loading.
  const pathPrefix = location.pathname.split('/')[1] ?? '';
  const pathModule = (['files', 'crm', 'hr', 'installments', 'pos', 'coupons', 'cashback', 'mandob', 'quotations', 'cms', 'imagegen', 'delivery', 'manufacturing'] as ModuleKey[]).find(
    (m) => m === pathPrefix,
  );
  if (pathModule && !moduleEnabled(enabledModules, pathModule)) {
    return <Navigate to="/dashboard" replace />;
  }
  // Items gated by their own `module` (they live under always-on sections, e.g.
  // audit-log + price-lists under Settings/Sales) — match the deepest nav item
  // for the current path and honor its module flag.
  const itemModule = NAV.flatMap((s) => s.items)
    .filter((i) => i.module && (location.pathname === i.to || location.pathname.startsWith(`${i.to}/`)))
    .map((i) => i.module)[0];
  if (itemModule && !moduleEnabled(enabledModules, itemModule)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn('erp-shell text-fg', navOpen && 'nav-open', kiosk && 'kiosk')} dir={dir}>
        <BrandFavicon />
        <div className="window">
          <ImpersonationBanner />
          <BillingBanner />

          {/* ---- Titlebar (window chrome + tools) ---- */}
          <header className="titlebar">
            <button
              type="button"
              className="icon-btn tb-menu"
              onClick={() => setNavOpen((o) => !o)}
              aria-label={t('actions.toggleNav', { defaultValue: 'Toggle navigation' })}
              aria-expanded={navOpen}
            >
              <Menu aria-hidden="true" />
            </button>
            <span className="tb-logo" aria-hidden="true">
              {tenant?.logoUrl ? (
                <img src={tenant.logoUrl} alt="" className="h-6 w-6 rounded-md object-cover" referrerPolicy="no-referrer" />
              ) : (
                <BrandMark className="h-6 w-6 rounded-md" />
              )}
            </span>
            <span className="tb-sep" aria-hidden="true" />
            <div className="tb-title">{title}</div>
            {/* Quick-access mega menu — every visible section's pages, one click away. */}
            {!kiosk && <TopNavMenu sections={visibleNav} />}
            <span className="tb-spacer" />

            <button type="button" className="search" onClick={() => setCmdOpen(true)} aria-label={t('actions.search', { defaultValue: 'Search' })}>
              <Search aria-hidden="true" />
              <span className="search-placeholder">{t('actions.search', { defaultValue: 'Search…' })}</span>
              <span className="kbd" aria-hidden="true">⌘K</span>
            </button>

            <div className="titlebar-tools">
              <Link
                className="icon-btn"
                to="/dashboard"
                aria-label={t('nav.dashboard', { defaultValue: 'Home' })}
                title={t('nav.dashboard', { defaultValue: 'Home' })}
              >
                <Home aria-hidden="true" />
              </Link>
              {posEnabled && (
                <Link
                  className="icon-btn"
                  to="/pos/register"
                  style={{ width: 'auto', paddingInline: 8, gap: 4 }}
                  aria-label={t('actions.openPos', { defaultValue: 'Open POS' })}
                  title={t('actions.openPos', { defaultValue: 'Open POS' })}
                >
                  <Store aria-hidden="true" />
                  <span style={{ fontWeight: 700, fontSize: 12 }}>POS</span>
                </Link>
              )}
              {/* On the register: stock transfer + receipt/payment vouchers, each
                  in a NEW tab so the cashier's open cart stays put. */}
              {location.pathname === '/pos/register' &&
                POS_QUICK_LINKS.filter(
                  (q) => permissions.includes(q.perm) && (!q.module || moduleEnabled(enabledModules, q.module)),
                ).map((q) => (
                  <a
                    key={q.to}
                    className="icon-btn"
                    href={q.to}
                    target="_blank"
                    rel="noreferrer"
                    style={{ width: 'auto', paddingInline: 8, gap: 4 }}
                    title={t(q.labelKey)}
                  >
                    <q.icon aria-hidden="true" />
                    <span style={{ fontWeight: 700, fontSize: 12 }}>{t(q.labelKey)}</span>
                  </a>
                ))}
              {moduleEnabled(enabledModules, 'ai') && (
                <AILauncherButton open={aiOpen} onToggle={() => setAiOpen((v) => !v)} />
              )}
              {cmsEnabled && storeSlug && (
                <a
                  className="icon-btn"
                  href={`/s/${storeSlug}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t('actions.viewStorefront', { defaultValue: 'Open storefront' })}
                  title={t('actions.viewStorefront', { defaultValue: 'Open storefront' })}
                >
                  <Globe aria-hidden="true" />
                </a>
              )}
              <button
                type="button"
                className="icon-btn tb-extra"
                onClick={() => setNumerals(numerals === 'latn' ? 'arab' : 'latn')}
                aria-label={t('actions.switchNumerals')}
                title={t('actions.switchNumerals')}
              >
                <span style={{ fontWeight: 700, fontSize: 12 }}>{numerals === 'arab' ? '٠١٢' : '012'}</span>
              </button>

              {enabledLanguages.length > 1 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="icon-btn"
                      style={{ width: 'auto', paddingInline: 8, gap: 4 }}
                      aria-label={t('actions.switchLanguage')}
                      title={t('actions.switchLanguage')}
                    >
                      <LocaleFlag code={curLang} className="h-4 w-auto rounded-[2px] ring-1 ring-border/50" />
                      <ChevronDown className="opacity-70" aria-hidden="true" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {enabledLanguages.map((l) => (
                      <DropdownMenuItem
                        key={l.code}
                        onSelect={() => void setLocale(l.code)}
                        className={`cursor-pointer gap-2${curLang === l.code ? ' font-semibold text-brand' : ''}`}
                      >
                        <LocaleFlag code={l.code} className="h-4 w-auto rounded-[2px] ring-1 ring-border/50" />
                        <span className="flex-1" dir={l.isRtl ? 'rtl' : 'ltr'}>{l.nativeName}</span>
                        {curLang === l.code && <Check className="h-4 w-4" aria-hidden="true" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              <ZoomControls />
              <ThemeToggle />
              <span className="tb-extra"><RateSwitcher /></span>
              <span className="tb-extra"><PaletteSwitcher /></span>

              {/* Staff notifications — bell + unread badge + recent-events dropdown. */}
              <NotificationsBell />

              {/* User menu via shadcn DropdownMenu (Radix). */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="avatar-btn"
                    aria-label={t('actions.userMenu', { defaultValue: 'User menu' })}
                  >
                    <Avatar className="h-8 w-8 border border-border">
                      <AvatarFallback>{initials(user?.fullName ?? user?.email ?? '?')}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="normal-case tracking-normal">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-fg">{user?.fullName ?? '—'}</span>
                      <span className="text-xs text-fg-muted">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings/profile" className="cursor-pointer">
                      <UserCheck className="h-4 w-4 text-fg-muted" aria-hidden="true" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  {permissions.includes('tenants.read') && (
                    <DropdownMenuItem asChild>
                      <Link to="/settings/tenant" className="cursor-pointer">
                        <Settings className="h-4 w-4 text-fg-muted" aria-hidden="true" />
                        {t('nav.tenant')}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onLogout} className="cursor-pointer">
                    <LogOut className="h-4 w-4 text-fg-muted" aria-hidden="true" />
                    {t('actions.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* ---- App grid: sidebar + main ---- */}
          <div className="app">
            <aside className={cn('side', rail && 'side--rail')}>
              <div className="brand">
                <div className="brand-mark">
                  {tenant?.logoUrl ? (
                    <img src={tenant.logoUrl} alt="" className="h-full w-full rounded-md object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <BrandMark className="h-full w-full" />
                  )}
                </div>
                <div>
                  <div className="brand-name">{tenant?.name ?? brandName}</div>
                  <div className="brand-sub">{siteTagline?.trim() || t('app.tagline', { defaultValue: 'Finance & Retail' })}</div>
                </div>
              </div>

              {/* Fold every section at once. Hidden in the rail, where there are no
                  expanded sections for it to act on. */}
              {!rail && (
                <button
                  type="button"
                  className="nav-rail-toggle"
                  onClick={() => collapseAll(visibleNav)}
                  title={t('nav.collapseAll', { defaultValue: 'Close all menus' })}
                >
                  <ChevronsDownUp className="nav-rail-toggle-ico" aria-hidden="true" />
                  <span className="nav-rail-toggle-label">{t('nav.collapseAll', { defaultValue: 'Close all menus' })}</span>
                </button>
              )}

              {/* Shrink / expand the whole sidebar. Desktop-only: on mobile the sidebar
                  is already a drawer, which solves the same problem better. */}
              <button
                type="button"
                className="nav-rail-toggle"
                onClick={() => setRail((v) => !v)}
                aria-pressed={rail}
                title={rail
                  ? t('nav.expandSidebar', { defaultValue: 'Expand the menu' })
                  : t('nav.collapseSidebar', { defaultValue: 'Shrink the menu' })}
              >
                {rail
                  ? <ChevronsRight className="nav-rail-toggle-ico" aria-hidden="true" />
                  : <ChevronsLeft className="nav-rail-toggle-ico" aria-hidden="true" />}
                <span className="nav-rail-toggle-label">{t('nav.collapseSidebar', { defaultValue: 'Shrink the menu' })}</span>
              </button>

              <nav aria-label={t('nav.primaryAria', { defaultValue: 'Primary' })}>
                {visibleNav.map((section) => {
                  const open = isGroupOpen(section.labelKey);
                  const SectionIcon = section.icon;
                  // ── Rail: one icon per section; its items open in a flyout. ──
                  if (rail) {
                    const hasActive = section.labelKey === activeSection;
                    return (
                      <button
                        key={section.labelKey}
                        type="button"
                        className={cn('nav-rail-btn', hasActive && 'active')}
                        aria-label={t(section.labelKey, { defaultValue: section.labelKey.split('.').pop() })}
                        title={t(section.labelKey, { defaultValue: section.labelKey.split('.').pop() })}
                        aria-expanded={flyout?.key === section.labelKey}
                        onClick={(e) => {
                          if (flyout?.key === section.labelKey) { setFlyout(null); return; }
                          // Coordinates are taken from the button and the panel is rendered
                          // OUTSIDE .side — which has backdrop-filter, and that makes it the
                          // containing block for fixed children, so a flyout nested inside
                          // would be positioned against the sidebar and clipped by its
                          // overflow-y: auto.
                          // Vertical anchor from the BUTTON, horizontal from the SIDEBAR:
                          // the button is inset inside the 64px rail, so anchoring x to it
                          // tucks the panel under the sidebar's edge.
                          const r = e.currentTarget.getBoundingClientRect();
                          const s = e.currentTarget.closest('.side')?.getBoundingClientRect();
                          setFlyout({
                            key: section.labelKey,
                            top: r.top,
                            left: dir === 'rtl' ? (s?.left ?? r.left) : (s?.right ?? r.right),
                          });
                        }}
                      >
                        {SectionIcon && <SectionIcon aria-hidden="true" />}
                      </button>
                    );
                  }
                  return (
                    <div
                      className={cn(
                        'nav-group',
                        !open && 'nav-group--collapsed',
                        section.items.length > 1 && 'nav-group--multi',
                      )}
                      key={section.labelKey}
                    >
                      <button
                        type="button"
                        className="nav-cap"
                        onClick={() => toggleGroup(section.labelKey, open)}
                        aria-expanded={open}
                      >
                        <span className="nav-cap-label">
                          {SectionIcon && (
                            <span className="nav-cap-ico">
                              <SectionIcon aria-hidden="true" />
                            </span>
                          )}
                          {t(section.labelKey, { defaultValue: section.labelKey.split('.').pop() })}
                        </span>
                        <ChevronDown className="nav-cap-chev" aria-hidden="true" />
                      </button>
                      <div className="nav-group-items">
                        {section.items.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.to);
                          return (
                            <Link
                              key={item.to}
                              to={item.to}
                              className={cn('nav-item', active && 'active')}
                              aria-current={active ? 'page' : undefined}
                              onClick={closeNavOnMobile}
                            >
                              <span className="nav-ico"><Icon aria-hidden="true" /></span>
                              <span className="truncate">{t(item.labelKey)}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </nav>

              <div className="org">
                <div className="org-logo">
                  {tenant?.logoUrl ? (
                    <img src={tenant.logoUrl} alt="" referrerPolicy="no-referrer" />
                  ) : (
                    <BrandMark className="h-full w-full" />
                  )}
                </div>
                <div className="org-meta">
                  <div className="org-name">{tenant?.name ?? '—'}</div>
                  <div className="org-sub">{user?.email ?? ''}</div>
                </div>
              </div>
            </aside>

            {/* The rail's flyout. A SIBLING of .side on purpose: .side sets
                backdrop-filter, which makes it the containing block for position:fixed
                descendants, so a panel nested inside would anchor to the sidebar and be
                clipped by its overflow-y: auto. Rendered here it is free of both. */}
            {rail && flyout && (() => {
              const section = visibleNav.find((s) => s.labelKey === flyout.key);
              if (!section) return null;
              return (
                <>
                  <div className="nav-fly-scrim" onClick={() => setFlyout(null)} aria-hidden="true" />
                  <div
                    className="nav-fly"
                    style={{
                      top: Math.min(flyout.top, Math.max(8, window.innerHeight - 40 - section.items.length * 42)),
                      ...(dir === 'rtl' ? { right: window.innerWidth - flyout.left } : { left: flyout.left }),
                    }}
                    role="group"
                    aria-label={t(section.labelKey, { defaultValue: section.labelKey.split('.').pop() })}
                  >
                    <div className="nav-fly-cap">{t(section.labelKey, { defaultValue: section.labelKey.split('.').pop() })}</div>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.to);
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={cn('nav-item', active && 'active')}
                          aria-current={active ? 'page' : undefined}
                          onClick={() => { setFlyout(null); closeNavOnMobile(); }}
                        >
                          <span className="nav-ico"><Icon aria-hidden="true" /></span>
                          <span className="truncate">{t(item.labelKey)}</span>
                        </Link>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            <main className="main" dir={dir}>
              <div className="scroll">
                <div className="wrap">
                  <DirectionScope dir={dir}>
                    <RouteTransition>{children}</RouteTransition>
                  </DirectionScope>
                </div>
              </div>
            </main>
          </div>

          {/* Mobile drawer backdrop — only interactive under the drawer breakpoint (CSS).
              Lives inside .window so it shares the sidebar's stacking context. */}
          <div className="nav-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
        </div>

        {moduleEnabled(enabledModules, 'ai') && <AILauncher open={aiOpen} onOpenChange={setAiOpen} />}
        {/* Global ⌘K command palette. */}
        <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
        {/* Global toast surface — call `toast.success(...)` from anywhere. */}
        <Toaster position="top-right" richColors closeButton />
      </div>
    </TooltipProvider>
  );
}

/** First letter of the first two whitespace-separated tokens, uppercased. */
function initials(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || '?';
}
